<script setup>
import { computed } from 'vue'
import AppBadge from '@/components/atoms/AppBadge'
import AppButton from '@/components/atoms/AppButton'
import AppSkeleton from '@/components/atoms/AppSkeleton'
import AppCarousel from '@/components/molecules/AppCarousel'
import HelpHubStepCard from './HelpHubStepCard.vue'
import { useStrapiStore } from '@/stores/StrapiStore'
import { useGetStarted } from '@/hooks/useGetStarted'
import { useHelpHub } from '@/hooks/useHelpHub'
import { safeCapture } from '@/helpers/posthog'
import { GETTING_STARTED_SURFACE, POSTHOG_EVENTS, HELP_HUB_GET_STARTED_SLUG } from '@/constants'
import stepImage from '@/assets/images/help-hub/get-started-step.jpg'

const strapiStore = useStrapiStore()
const { steps, stepCount, completedCount, activeKey, done, canComplete, completeStep, completing } = useGetStarted()
const { helpLink } = useHelpHub()

// Only the steps left to do (the rest sit in the "View all" list); once every step is done, all of them, to go back to.
const shown = computed(() => (done.value ? steps.value : steps.value.filter((step) => !step.completed)))

const trackStep = (step) => safeCapture(POSTHOG_EVENTS.GETTING_STARTED_CARD_CLICKED, { step_key: step.key })
</script>

<template>
  <!-- The section's shape while the steps load: its title row, then the cards as the design's blocks at our width (Figma 29796:69280). -->
  <section v-if="strapiStore.getStartedLoading" role="status" class="flex flex-col gap-y-4 px-4 pt-2 pb-5">
    <span class="sr-only">Loading Get started</span>

    <div class="flex items-center justify-between gap-x-2">
      <div class="flex items-center gap-x-2">
        <AppSkeleton width="w-24" height="h-5" />
        <AppSkeleton width="w-28" height="h-[22px]" rounded="rounded-full" />
      </div>

      <AppSkeleton width="w-14" height="h-4" />
    </div>

    <!-- Bleeds like the carousel, so the second card peeks in; a card is its picture and two lines under it. -->
    <div class="-mx-4 flex gap-x-6 overflow-hidden px-4">
      <AppSkeleton v-for="card in 2" :key="card" class="shrink-0" width="w-72" height="h-[232px]" rounded="rounded-xl" />
    </div>
  </section>

  <section v-else class="flex flex-col gap-y-4 px-4 pt-2 pb-5">
    <header class="flex items-center justify-between gap-x-2">
      <div class="flex items-center gap-x-2">
        <h3 class="text-base font-semibold text-gray-900">Get started</h3>

        <AppBadge size="xs" variant="tint-faint">
          <template v-if="done">Completed</template>
          <template v-else>{{ completedCount }} of {{ stepCount }} completed</template>
        </AppBadge>
      </div>

      <AppButton
        as="router-link"
        :to="helpLink(HELP_HUB_GET_STARTED_SLUG)"
        variant="link"
        size="sm"
        class="-mr-2"
      >
        View all
      </AppButton>
    </header>

    <!-- Bleeds to the panel's edges so the next card peeks in. -->
    <AppCarousel arrows="overlay" :bleed="16" gap="gap-x-6" aria-label="Get started">
      <!-- Each step's key stands in for its help page slug. -->
      <HelpHubStepCard
        v-for="step in shown"
        :key="step.key"
        class="w-72"
        :to="helpLink(step.key)"
        :number="step.number"
        :title="step.title"
        :description="step.helpDescription"
        :step-key="step.key"
        :image="stepImage"
        :active="step.key === activeKey"
        :locked="step.locked"
        :completable="canComplete(step)"
        :completing="completing === step.key"
        @click="trackStep(step)"
        @complete="completeStep(step, GETTING_STARTED_SURFACE.HUB_CARD)"
      />
    </AppCarousel>
  </section>
</template>
